import { useEffect, useRef } from "react";
import type { FiatPrice } from "@b2bportal/air-shopping-api";
import { logger } from "@hopper-b2b/api";
import {
  useExperiment,
  useGetExperimentVariant,
} from "@hopper-b2b/experiments";
import { useI18nContext } from "@hopper-b2b/i18n";
import { TripCategory } from "@hopper-b2b/types";
import { ButtonWrap } from "@hopper-b2b/ui";
import { DisplayedPrice } from "@hopper-b2b/wallet";
import { Typography } from "@material-ui/core";
import clsx from "clsx";
import { EXPERIMENTS } from "../../App";
import ArrowRight from "../../assets/client/arrow-right.svg?react";
import { EXACT_CHECKOUT_TOTAL_FORMAT } from "../../utils/prices";
import { tapHandlerLog } from "../../utils/tapHandlerLog";
import { logElementStack } from "../../utils/touchDiagnostics";
import { useTapHandlers } from "../../utils/useTapHandlers";
import "./styles.scss";

interface ISharedFlightCtaSectionProps {
  className?: string;
  discountedPrice: FiatPrice | null;
  totalPrice: FiatPrice;
  tripCategory: TripCategory;
  onClick: () => void;
  buttonDisabled?: boolean;
  showCombinedTotal?: boolean;
  totalPriceString?: string;
  installmentCopyKey?: string;
  buttonContent?: JSX.Element;
  buttonClassname?: string;
}

const SharedFlightCtaSection = ({
  className,
  discountedPrice,
  totalPrice,
  tripCategory,
  onClick,
  totalPriceString,
  buttonDisabled = false,
  showCombinedTotal = false,
  installmentCopyKey,
  buttonContent = null,
  buttonClassname,
}: ISharedFlightCtaSectionProps) => {
  const { t, formatFiatCurrency } = useI18nContext();

  // Combined checkout total: exact 2dp to match NuPay. Per-traveler/shop: caller string or DisplayedPrice 0dp fallback.
  const displayedTotalString = showCombinedTotal
    ? formatFiatCurrency(totalPrice, EXACT_CHECKOUT_TOTAL_FORMAT)
    : totalPriceString;
  const blackFridayEnabled = useExperiment(EXPERIMENTS.NUBANK_BLACK_FRIDAY);
  const nubankMaxInstallments = useGetExperimentVariant(
    EXPERIMENTS.MAX_INSTALLMENTS
  );
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { onClick: onTapClick, ...tapPointerHandlers } = useTapHandlers({
    onTap: onClick,
    disabled: buttonDisabled,
    logName: "flight_shop_cta",
    onLog: tapHandlerLog,
    recoverTapOnPointerCancel: true,
  });

  useEffect(() => {
    const bodyStyle = document.body.style.pointerEvents;
    if (bodyStyle === "none") {
      logger()?.warn(
        "[FlightShopCTA] body.style.pointerEvents stuck on 'none', resetting",
        {
          event: "pointer_events_reset",
          bodyClasses: document.body.className,
        }
      );
      document.body.style.pointerEvents = "";
    }
  }, []);

  useEffect(() => {
    logger()?.info("[FlightShopCTA] CTA mounted", {
      event: "flight_shop_cta_mount",
      buttonDisabled,
      hasOnClick: typeof onClick === "function",
      url: window.location.href,
    });
  }, [buttonDisabled, onClick]);

  useEffect(() => {
    if (buttonRef.current) {
      logElementStack(buttonRef.current, "flight_shop_cta");
    }
  }, []);

  return (
    <div className={clsx(className, "fare-details-bottom-wrapper")}>
      <div className="fare-details-price-summary-wrapper">
        <div className="fare-details-summary-section">
          <div className="description-text">
            {tripCategory === TripCategory.ONE_WAY
              ? t(
                  showCombinedTotal
                    ? "oneWayTripCategoryTotal"
                    : "oneWayPerTraveler"
                )
              : t(
                  showCombinedTotal
                    ? "roundTripCategoryTotal"
                    : "roundTripPerTraveler"
                )}
          </div>
          <div className="price-text">
            <Typography className="content" variant="body1">
              <DisplayedPrice
                discountedCostFiat={discountedPrice}
                totalCostFiat={totalPrice}
                totalCostString={displayedTotalString}
              />
            </Typography>
            <div className="installments-badge">
              {/* Black Friday Banner, Remove after 11/12/2024 */}
              {blackFridayEnabled ? (
                <p>
                  {t("upToInterestFreeInstallmentsShort", {
                    count: parseInt(nubankMaxInstallments, 10),
                  })}
                  {" - "}
                  {t("blackFriday.title")}
                </p>
              ) : (
                <p>
                  {t(
                    installmentCopyKey
                      ? installmentCopyKey
                      : "upToInterestFreeInstallments",
                    {
                      count: parseInt(nubankMaxInstallments, 10),
                    }
                  )}
                </p>
              )}
            </div>
          </div>
        </div>
        <ButtonWrap
          ref={buttonRef}
          className={clsx("fare-details-button-section", buttonClassname)}
          style={tapPointerHandlers.style}
          onClick={onTapClick}
          onPointerDown={tapPointerHandlers.onPointerDown}
          onPointerUp={tapPointerHandlers.onPointerUp}
          onPointerCancel={tapPointerHandlers.onPointerCancel}
        >
          {buttonContent ? (
            buttonContent
          ) : (
            <ArrowRight className={buttonDisabled ? "disabled" : ""} />
          )}
        </ButtonWrap>
      </div>
    </div>
  );
};

export default SharedFlightCtaSection;
